import { useState } from 'react';
import {
  withStyles,
  createTableProps,
  MaterialSymbol,
  ClientAccount,
  Box,
  GetColorStatus,
  capitalizeFirstLetterOfWords,
} from 'vopay';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './useTableConfig.module.scss';
import TransactionStatus from './TransactionStatus/TransactionStatus';
import useClientAccountList from './useClientAccountList';
import { Balances, Labels, Limits } from './TableCells';

const cn = withStyles(styles);

export default function useTableConfig(accounts: ClientAccount[]) {
  const [emailOrPhone, setEmailOrPhone] = useState<'email' | 'phone'>('email');
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.ClientAccountList' });

  const {
    account: { country },
    hostPaths,
  } = useClientAccountList();

  return createTableProps({
    id: 'client-accounts',
    zebra: true,
    rows: accounts.map((c) => ({
      id: c.ClientAccountID,
      data: c,
    })),
    columns: [
      {
        id: 'ClientAccountID',
        header: t('id', { defaultValue: 'ID' }),
        align: 'left',
        style: {
          whiteSpace: 'nowrap',
        },
        template: ({ data }) => data.ClientAccountID,
      },
      {
        id: 'ClientName',
        header: t('name', { defaultValue: 'Name' }),
        align: 'left',
        style: {
          whiteSpace: 'nowrap',
        },
        template: ({ data }) => (
          <Link to={hostPaths.accountDetail(data.ClientAccountID)} className={cn('link')}>
            {data.ClientName}
          </Link>
        ),
      },
      {
        id: 'status',
        align: 'left',
        template: ({ data }) => (
          <Box flx="row" ai="center">
            <TransactionStatus
              status={GetColorStatus(data.IsActive ? 'active' : 'inactive')}
              label={capitalizeFirstLetterOfWords(
                data.IsActive
                  ? t('Status.active', { defaultValue: 'Active' })
                  : t('Status.inactive', { defaultValue: 'Inactive' }),
              )}
            />
          </Box>
        ),
        header: t('status', { defaultValue: 'Status' }),
      },
      {
        id: 'Email',
        header: (
          <Box gap="sm" ai="center">
            {t('email', { defaultValue: 'Email' })}
            <MaterialSymbol icon={'switch_left'} fillAmount={1} size={'24px'} />
          </Box>
        ),
        align: 'left',
        style: {
          whiteSpace: 'nowrap',
        },
        visible: emailOrPhone === 'email',
        template: ({ data }) => data.EmailAddress,
        onHeaderClick: () => {
          setEmailOrPhone('phone');
        },
      },
      {
        id: 'PhoneNumber',
        header: (
          <Box gap="sm" ai="center">
            {t('phone', { defaultValue: 'Phone Number' })}
            <MaterialSymbol icon={'switch_right'} fillAmount={1} size={'24px'} />
          </Box>
        ),
        align: 'left',
        style: {
          whiteSpace: 'nowrap',
        },
        visible: emailOrPhone === 'phone',
        template: ({ data }) => formatPhoneNumber(data.PhoneNumber),
        onHeaderClick: () => {
          setEmailOrPhone('email');
        },
      },
      {
        id: 'type',
        header: t('clientAccountType', { defaultValue: 'Client Account Type' }),
        align: 'left',
        template: ({ data }) =>
          data.IsBusiness
            ? t('ClientAccountType.business', { defaultValue: 'Business' })
            : t('ClientAccountType.individual', { defaultValue: 'Individual' }),
      },
      {
        id: 'limits',
        header: t('transactionLimit', { defaultValue: 'Transaction Limit' }),
        align: 'left',
        style: {
          whiteSpace: 'nowrap',
        },
        template: ({ data }) => <Limits country={country} limit={data.TransactionLimit} balances={data.Balances} />,
      },
      {
        id: 'Balances',
        header: t('balances', { defaultValue: 'Balances' }),
        align: 'left',
        style: {
          whiteSpace: 'nowrap',
        },
        template: ({ data }) => <Balances country={country} balances={data.Balances} />,
      },
      {
        id: 'Labels',
        header: t('labels', { defaultValue: 'Labels' }),
        align: 'left',
        template: ({ data }) => <Labels labels={data.Labels} />,
      },
    ],
  });
}

function formatPhoneNumber(phoneNumber: string): string {
  // Remove all non-numeric characters
  const cleaned = `${phoneNumber}`.replace(/\D/g, '');

  // Check the length of the cleaned number
  if (cleaned.length < 10 || cleaned.length > 12) {
    // If the length is invalid, return the original input
    return phoneNumber;
  }

  let countryCode = '1'; // Default country code
  let localNumber = cleaned;

  if (cleaned.length === 11) {
    // If the length is 11, assume the first digit is the country code

    countryCode = cleaned[0];
    localNumber = cleaned.slice(1);
  } else if (cleaned.length === 12) {
    // If the length is 12, assume the first two digits are the country code
    countryCode = cleaned.slice(0, 2);
    localNumber = cleaned.slice(2);
  }

  // Format the local number
  const match = /^(\d{3})(\d{3})(\d{4})$/.exec(localNumber);

  if (match) {
    return `+${countryCode} (${match[1]}) ${match[2]}-${match[3]}`;
  }

  return phoneNumber;
}
